import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {DataGridPro} from '@mui/x-data-grid-pro';
import {Box, Tab, Tabs} from '@mui/material';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {useSelector} from 'react-redux';
import {IMediaTrack} from '../../../../dto/MediaTrack.dto';
import {
  IConditionalOperators,
  IDataGridProSearchSort,
  defaultDataGridProSearchSort,
} from '../../../../dto/Html.dto';
import {companyIdSelector} from '../../../../store/Slices/companySlice';
import {useRqMediaTracksPaginate} from '../../../../react_query/media_tracks/ListMediaTrackPaginatPro';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import {DataGridCustPagination} from '../../../../components/utlis/DataGrid/Pagination';
import {SongSelectionAddButton} from '../../../../components/Button/SongSelectionAddButton';
import {SongSelectionRemoveButton} from '../../../../components/Button/SongSelectionRemoveButton';
import {GridDurationColumn} from '../../../../components/grid/GridColumns';
import {tabsStyleRadioMode} from '../../../../components/styles/TabStyles';
import FuzzySearchSong from '../../search/FuzzySearchSong';

type Props = {
  selectedTracks: IMediaTrack[];
  onSelect: (track: IMediaTrack) => void;
  onDeselect: (track: IMediaTrack) => void;
  isAddDisabled?: boolean;
  stationId?: number;
  setTabType?: React.Dispatch<React.SetStateAction<string>>;
  manualHeight?: number;
};

const WalkupMusicSelectionTable = ({
  selectedTracks,
  onSelect,
  onDeselect,
  isAddDisabled,
  stationId,
  setTabType,
  manualHeight = 500,
}: Props) => {
  const [selectedTrackIds, setSelectedTrackIds] = useState<number[]>([]);
  const [queryTab, setQueryTab] = useState<string>('all');
  const [query, setQuery] = React.useState<
    IDataGridProSearchSort & {track_artist: string; track_name: string} & (
        | {
            track_length: number;
            track_length_condition: IConditionalOperators;
          }
        | {track_length?: undefined; track_length_condition?: undefined}
      )
  >({
    ...defaultDataGridProSearchSort,
    track_artist: '',
    track_name: '',
    track_category: 2,
  });
  const companyId = useSelector(companyIdSelector);
  const {data, isLoading} = useRqMediaTracksPaginate(query, companyId);

  const handleFuzzyChange = useCallback(
    (filters: {track_artist: string; track_name: string; fuzzy: boolean}) => {
      setQuery(prev => ({
        ...prev,
        ...filters,
      }));
    },
    [setQuery],
  );

  const handleFuzzyReset = useCallback(() => {
    setQuery(prev => ({
      ...prev,
      track_artist: '',
      track_name: '',
      page: 0,
    }));
  }, [setQuery]);

  const handlePagination = useCallback((paginate: GridPaginationModel) => {
    setQuery(prev => ({...prev, ...paginate}));
  }, []);

  const handleSortModelChange = (model: GridSortModel) => {
    setQuery(prev => ({...prev, sort: model}));
  };

  const handleSongAdd = useCallback(
    (musicTrack: IMediaTrack) => {
      onSelect(musicTrack);
    },
    [onSelect],
  );

  const handleSongRemove = useCallback(
    (musicTrack: IMediaTrack) => {
      onDeselect(musicTrack);
    },
    [onDeselect],
  );

  const handleHitterPithcerToggle = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      setQuery(prev => {
        const updatedQuery = {...prev};
        updatedQuery.track_category = 2;

        const resetTrackLengthAndStation = () => {
          delete updatedQuery.track_length;
          delete updatedQuery.track_length_condition;
          delete updatedQuery.station_id;
        };

        switch (newValue) {
          case 'all':
            resetTrackLengthAndStation();
            break;

          case 'clip':
            resetTrackLengthAndStation();
            updatedQuery.track_category = 4;
            updatedQuery.station_id = stationId ? [stationId] : [];
            break;

          default: // 'hitter' or 'pitcher'
            updatedQuery.track_length = 45;
            updatedQuery.track_length_condition =
              newValue === 'hitter' ? 'lte' : 'gt';
            delete updatedQuery.station_id;
            break;
        }
        return updatedQuery;
      });
      setTabType?.(newValue);
      setQueryTab(newValue);
    },
    [setQuery, stationId, setTabType],
  );

  const columns: GridColDef[] = useMemo(
    () =>
      [
        {
          field: 'track_artist',
          headerName: 'Artist',
          flex: 1,
        },
        {
          field: 'track_name',
          headerName: 'Song Title',
          flex: 1,
        },
        GridDurationColumn,
        {
          field: 'type',
          headerName: 'Type',
          flex: 1,
          sortable: false,
          renderCell: (params: GridRenderCellParams) =>
            params.row.track_category === 4
              ? 'Custom Clip'
              : params.row.duration > 45
              ? 'Pitcher'
              : 'Hitter',
        },
        {
          field: 'url',
          type: 'actions',
          headerName: 'Play',
          width: 54,
          sortable: false,
          renderCell: (params: GridRenderCellParams<IMediaTrack>) =>
            params.row.url ? (
              <MusicPreviewBtn
                id={params.row.id}
                url={params.row.url}
                track_name={params.row.track_name}
                track_artist={params.row.track_artist}
              />
            ) : null,
        },

        {
          type: 'actions',
          field: 'actions',
          headerName: '',
          hideable: false,
          sortable: false,
          width: 125,
          renderCell: (params: GridRenderCellParams<IMediaTrack>) =>
            selectedTrackIds.includes(params.row.id) ? (
              <SongSelectionRemoveButton
                onClick={() => handleSongRemove(params.row)}
              />
            ) : (
              <SongSelectionAddButton
                onClick={() => handleSongAdd(params.row)}
                disabled={isAddDisabled}
              />
            ),
        },
      ].map(column => ({
        ...column,
        filterable: false,
      })),
    [handleSongAdd, handleSongRemove, isAddDisabled, selectedTrackIds],
  );

  useEffect(() => {
    setSelectedTrackIds(selectedTracks.map(o => o.id));
  }, [selectedTracks]);

  return (
    <>
      <Box className='flex justify-between'>
        <Tabs
          value={queryTab}
          onChange={handleHitterPithcerToggle}
          variant='scrollable'
          sx={tabsStyleRadioMode}
          aria-label='tabs'>
          <Tab value='hitter' label='Hitter' />
          <Tab value='all' label='Show All' />
          <Tab value='pitcher' label='Pitcher' />
          <Tab value='clip' label='Custom Clips' />
        </Tabs>
      </Box>
      <FuzzySearchSong
        onReset={handleFuzzyReset}
        onFuzzySearchChange={handleFuzzyChange}
        trackArtist={query.track_artist ?? ''}
        trackName={query.track_name ?? ''}
      />
      <Box sx={{mt: 1, height: manualHeight}}>
        <DataGridPro
          rows={data?.data ?? []}
          columns={columns}
          loading={isLoading}
          rowCount={data?.total ?? 0}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{page: query.page, pageSize: query.pageSize}}
          paginationMode='server'
          pagination={true}
          onPaginationModelChange={handlePagination}
          sortingMode='server'
          onSortModelChange={handleSortModelChange}
          density='compact'
          disableRowSelectionOnClick
          disableColumnMenu
          slots={{
            pagination: DataGridCustPagination,
          }}
          slotProps={{
            columnsPanel: {
              disableHideAllButton: true,
              disableShowAllButton: true,
            },
          }}
        />
      </Box>
    </>
  );
};

export default WalkupMusicSelectionTable;
